/**
 * YUMIX — Indestructible Scan Telemetry (Frontend).
 *
 * Mission: capture EVERY silent failure of the meal scanner that the backend
 * middleware can't see (offline drops, page unloads, component unmounts, UI
 * locks). Pipeline:
 *
 *   trackEvent(name, data)
 *       │
 *       ├──> push to localStorage ring buffer (survives reload/crash)
 *       ├──> attempt immediate POST /admin/scan-health/bulk-ingest
 *       ├──> on success: drain buffer
 *       └──> on failure (offline, 5xx): keep buffer, retry later
 *
 * Triggers that flush the buffer:
 *   • App boot (errorLogger.install() → scanTelemetry.flushSoon())
 *   • Network back online ('online' event)
 *   • Page hidden / unload (navigator.sendBeacon)
 *   • Successful event (chains into a flush)
 *
 * Heartbeat: while a scan is in-flight we ping POST /scan-heartbeat every
 * 30 s with the idempotency_key. The admin dashboard can later cross-reference
 * heartbeats that never produced a final outcome → "ghost" scans.
 *
 * All paths swallow errors. Telemetry MUST NEVER fail the user's scan.
 */
import { api } from '../context/AppContext';

const BUFFER_KEY = 'yumix_scan_telemetry_buffer_v1';
const BUFFER_MAX = 100;
const FLUSH_DEBOUNCE_MS = 1500;
const HEARTBEAT_INTERVAL_MS = 30000;

// --- helpers ---------------------------------------------------------------
function readBuffer() {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeBuffer(arr) {
  try {
    // Cap size to avoid runaway localStorage growth on heavy crash loops.
    const trimmed = arr.slice(-BUFFER_MAX);
    localStorage.setItem(BUFFER_KEY, JSON.stringify(trimmed));
  } catch { /* private mode / quota — silent */ }
}

function clearBuffer() {
  try { localStorage.removeItem(BUFFER_KEY); } catch { /* noop */ }
}

function getApiBase() {
  // The shared axios instance uses REACT_APP_BACKEND_URL/api as baseURL.
  // For sendBeacon we need a full URL.
  const root = process.env.REACT_APP_BACKEND_URL || '';
  return `${root.replace(/\/$/, '')}/api`;
}

function getSessionToken() {
  try { return localStorage.getItem('session_token') || null; } catch { return null; }
}

// --- core API --------------------------------------------------------------
let _flushTimer = null;

/**
 * Record a frontend scan telemetry event. Persists to localStorage and
 * schedules a flush. Safe to call from anywhere — never throws.
 */
export function trackEvent(name, data = {}) {
  try {
    const entry = {
      event_name: String(name || 'unknown').slice(0, 80),
      data: data && typeof data === 'object' ? data : {},
      ts: new Date().toISOString(),
      url: (typeof window !== 'undefined' ? window.location.pathname : '').slice(0, 200),
      ua: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 300),
      online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
    };
    const buf = readBuffer();
    buf.push(entry);
    writeBuffer(buf);
    scheduleFlush();
  } catch { /* swallow */ }
}

/** Debounced flush — coalesces bursts. */
function scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flush();
  }, FLUSH_DEBOUNCE_MS);
}

/**
 * Try to send buffered events to the backend. Drains buffer on success.
 * Keeps buffer intact on failure so we retry later.
 */
export async function flush() {
  let batch = [];
  try {
    batch = readBuffer();
    if (!batch.length) return;
    await api.post('/admin/scan-health/bulk-ingest', { events: batch }, { timeout: 6000 });
    clearBuffer();
  } catch {
    // Keep the buffer for next flush. We do nothing — silence is golden here.
  }
}

/**
 * Sync beacon flush — used at page unload where async fetch would be killed.
 * Uses navigator.sendBeacon so the browser guarantees delivery even after
 * the tab is closed.
 */
export function flushBeacon() {
  try {
    const batch = readBuffer();
    if (!batch.length) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    const token = getSessionToken();
    const url = `${getApiBase()}/admin/scan-health/bulk-ingest${token ? `?t=${encodeURIComponent(token)}` : ''}`;
    const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
    const ok = navigator.sendBeacon(url, blob);
    if (ok) clearBuffer();
  } catch { /* swallow */ }
}

// --- heartbeat -------------------------------------------------------------
let _hb = null;
let _hbKey = null;

/** Start emitting a scan heartbeat every 30 s. Multiple starts replace. */
export function startScanSession(idempotencyKey, meta = {}) {
  endScanSession();
  _hbKey = idempotencyKey || null;
  // Fire one immediately so the backend sees the session even if the user
  // closes the tab before the first interval tick.
  pingHeartbeat(meta);
  _hb = setInterval(() => pingHeartbeat(meta), HEARTBEAT_INTERVAL_MS);
}

/** Stop the heartbeat (final outcome reached — success or failure). */
export function endScanSession() {
  if (_hb) {
    clearInterval(_hb);
    _hb = null;
  }
  _hbKey = null;
}

function pingHeartbeat(meta = {}) {
  if (!_hbKey) return;
  // Fire-and-forget — never blocks UI, never throws.
  try {
    api.post('/scan-heartbeat', {
      idempotency_key: _hbKey,
      ...meta,
    }, { timeout: 4000 }).catch(() => {});
  } catch { /* swallow */ }
}

// --- install ---------------------------------------------------------------
let _installed = false;

/**
 * Install global listeners: online → flush, unload → beacon flush.
 * Idempotent.
 */
export function install() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  // Flush whenever connectivity is restored.
  window.addEventListener('online', () => { flush(); });

  // Flush on tab hide (works on mobile where 'beforeunload' is unreliable).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });

  // Flush on hard unload as a last resort.
  window.addEventListener('pagehide', () => { flushBeacon(); });

  // First-boot flush — drain whatever was left from the previous session.
  setTimeout(() => flush(), 2500);
}

const ScanTelemetry = {
  trackEvent, flush, flushBeacon,
  startScanSession, endScanSession, install,
};

export default ScanTelemetry;
